import { useEffect, useRef, useState } from "react";
import { AR } from "js-aruco";
import { perspectiveWarp, orderCorners } from "./PerspectiveWarp";

function MarkerCanvas({
  imageUrl,
  onMarkersDetected,
  onImageClear,
  savedHost,
  onContinue,
  initialName,
  initialCreatedBy,
}) {
  const canvasRef = useRef(null);
  const [warpedImage, setWarpedImage] = useState(null);
  const [vehicleID, setVehicleID] = useState(null);
  const [name, setName] = useState(initialName || null);
  const [createdBy, setCreatedBy] = useState(initialCreatedBy || null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Dynamischer Threshold (Otsu-ähnlich)
  const calculateThreshold = (grayValues) => {
    const hist = new Array(256).fill(0);
    grayValues.forEach((val) => hist[val]++);
    const total = grayValues.length;

    let sum = 0;
    for (let i = 0; i < 256; i++) sum += i * hist[i];

    let sumB = 0,
      wB = 0,
      wF = 0,
      varMax = 0,
      threshold = 0;

    for (let t = 0; t < 256; t++) {
      wB += hist[t];
      if (wB === 0) continue;
      wF = total - wB;
      if (wF === 0) break;

      sumB += t * hist[t];
      const mB = sumB / wB;
      const mF = (sum - sumB) / wF;

      const varBetween = wB * wF * (mB - mF) ** 2;
      if (varBetween > varMax) {
        varMax = varBetween;
        threshold = t;
      }
    }
    return threshold;
  };

  useEffect(() => {
    if (!imageUrl) return;

    setIsLoading(true);
    setWarpedImage(null);

    const img = new Image();
    console.log("Image url: '"+imageUrl+"'");
    img.onerror = (event) => { setHasError(1); setIsLoading(false); console.log("error",event)};
    img.onload = () => {
      console.log("on load");

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      const width = img.width;
      const height = img.height;

      const borderWidth = 5;

      // Canvas größer machen für den Rahmen
      canvas.width = width + 2 * borderWidth;
      canvas.height = height + 2 * borderWidth;

      // Weißen Hintergrund/Rahmen zeichnen
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Bild mit Offset zeichnen
      ctx.drawImage(img, borderWidth, borderWidth, width, height);

      // Quadranten auf dem gesamten Canvas (inkl. Border)
      const quadrants = [
        { x: 0, y: 0, w: canvas.width / 2, h: canvas.height / 2 },
        {
          x: canvas.width / 2,
          y: 0,
          w: canvas.width / 2,
          h: canvas.height / 2,
        },
        {
          x: 0,
          y: canvas.height / 2,
          w: canvas.width / 2,
          h: canvas.height / 2,
        },
        {
          x: canvas.width / 2,
          y: canvas.height / 2,
          w: canvas.width / 2,
          h: canvas.height / 2,
        },
      ];

      let allMarkers = [];

      quadrants.forEach((q) => {
        // Grauwert-Array
        const imageData = ctx.getImageData(q.x, q.y, q.w, q.h);
        const grayValues = [];
        for (let j = 0; j < imageData.data.length; j += 4) {
          const gray =
            0.299 * imageData.data[j] +
            0.587 * imageData.data[j + 1] +
            0.114 * imageData.data[j + 2];
          grayValues.push(Math.round(gray));
        }

        const threshold = calculateThreshold(grayValues);

        // Binarisierung
        for (let j = 0; j < imageData.data.length; j += 4) {
          const gray =
            0.299 * imageData.data[j] +
            0.587 * imageData.data[j + 1] +
            0.114 * imageData.data[j + 2];
          const binary = gray > threshold ? 255 : 0;
          imageData.data[j] =
            imageData.data[j + 1] =
            imageData.data[j + 2] =
              binary;
        }

        ctx.putImageData(imageData, q.x, q.y);

        // Marker erkennen
        const detector = new AR.Detector();
        const markers = detector.detect(ctx.getImageData(q.x, q.y, q.w, q.h));

        // Marker-Position auf Gesamtcanvas anpassen
        markers.forEach((m) => {
          m.corners.forEach((c) => {
            c.x += q.x;
            c.y += q.y;
          });
        });

        allMarkers = allMarkers.concat(markers);

        // Quadrant blau markieren
        ctx.strokeStyle = "blue";
        ctx.lineWidth = 2;
        ctx.strokeRect(q.x, q.y, q.w, q.h);
      });

      // Marker rot markieren
      ctx.strokeStyle = "red";
      ctx.lineWidth = 3;
      allMarkers.forEach((marker) => {
        const corners = marker.corners;
        ctx.beginPath();
        ctx.moveTo(corners[0].x, corners[0].y);
        for (let i = 1; i < corners.length; i++)
          ctx.lineTo(corners[i].x, corners[i].y);
        ctx.closePath();
        ctx.stroke();

        // ID auf Canvas schreiben
        const cx = corners.reduce((sum, c) => sum + c.x, 0) / 4;
        const cy = corners.reduce((sum, c) => sum + c.y, 0) / 4;
        ctx.fillStyle = "red";
        ctx.font = "16px Arial";
        ctx.fillText(marker.id, cx - 8, cy + 5);
      });

      // Duplikate entfernen → nur ein Marker pro ID
      const uniqueMarkers = [];
      const seenIds = new Set();
      allMarkers.forEach((marker) => {
        if (!seenIds.has(marker.id)) {
          seenIds.add(marker.id);
          uniqueMarkers.push(marker);
        }
      });

      // Marker zurückgeben
      onMarkersDetected(uniqueMarkers);

      // Apply perspective warp if we have exactly 4 markers (the corners of the document)
      if (uniqueMarkers.length < 4) {
        setWarpedImage(null);
        setHasError(2);
      } else {
        // Sort markers by ID to ensure consistent corner selection
        const sortedMarkers = uniqueMarkers
          .slice(0, 4)
          .sort((a, b) => a.id - b.id);

        // Find the base marker (lowest ID, which should be divisible by 4)
        const baseMarker = sortedMarkers[0]; // First in sorted array = lowest ID
        const baseId = baseMarker.id;

        // Store the vehicle ID (base marker ID divided by 4)
        const calculatedVehicleID = Math.floor(baseId / 4);
        setVehicleID(calculatedVehicleID);

        // Use a specific corner from each marker based on its ID modulo 4
        // ID 0,4,8,12... -> corner 0
        // ID 1,5,9,13... -> corner 1
        // ID 2,6,10,14... -> corner 2
        // ID 3,7,11,15... -> corner 3
        const markerCorners = sortedMarkers.map((marker) => {
          const cornerIndex = marker.id % 4; // Use marker ID modulo 4 to determine corner
          return {
            x: marker.corners[cornerIndex].x - borderWidth,
            y: marker.corners[cornerIndex].y - borderWidth,
            id: marker.id,
          };
        });

        // Order these corners properly for perspective transform
        const orderedCorners = orderCorners(markerCorners);

        // Find where the base marker is positioned
        const baseMarkerIndex = orderedCorners.findIndex(
          (corner) => corner.id === baseId,
        );

        // Determine rotation needed based on where the base marker is
        // baseMarkerIndex: 0 = top-left, 1 = top-right, 2 = bottom-right, 3 = bottom-left
        const rotationMap = {
          0: 0, // Already top-left: no rotation
          1: 270, // Top-right: rotate 270° CCW (or 90° CW)
          2: 180, // Bottom-right: rotate 180°
          3: 90, // Bottom-left: rotate 90° CCW
        };

        const rotationDegrees = rotationMap[baseMarkerIndex] || 0;

        // Calculate output dimensions based on the distances between corners
        const width1 = Math.hypot(
          orderedCorners[1].x - orderedCorners[0].x,
          orderedCorners[1].y - orderedCorners[0].y,
        );
        const width2 = Math.hypot(
          orderedCorners[2].x - orderedCorners[3].x,
          orderedCorners[2].y - orderedCorners[3].y,
        );
        const height1 = Math.hypot(
          orderedCorners[3].x - orderedCorners[0].x,
          orderedCorners[3].y - orderedCorners[0].y,
        );
        const height2 = Math.hypot(
          orderedCorners[2].x - orderedCorners[1].x,
          orderedCorners[2].y - orderedCorners[1].y,
        );

        let outputWidth = Math.max(width1, width2);
        let outputHeight = Math.max(height1, height2);

        // Ensure landscape orientation: width should always be greater than height
        // If after rotation the image would be portrait, we need to swap dimensions
        const willBeLandscape =
          rotationDegrees === 0 || rotationDegrees === 180
            ? true
            : true;

        if (!willBeLandscape) {
          [outputWidth, outputHeight] = [outputHeight, outputWidth];
        }

        // Fixed output size for consistency
        const fixedWidth = 1200;
        const fixedHeight = Math.round(
          fixedWidth * (outputHeight / outputWidth),
        );

        try {
          const warpedCanvas = perspectiveWarp(
            img,
            orderedCorners,
            fixedWidth,
            fixedHeight,
          );

          if (rotationDegrees !== 0) {
            // Create a new canvas for rotation
            const rotatedCanvas = document.createElement("canvas");
            const rotCtx = rotatedCanvas.getContext("2d");

            // For 90° or 270° rotation, swap width and height
            if (rotationDegrees === 90 || rotationDegrees === 270) {
              rotatedCanvas.width = warpedCanvas.height;
              rotatedCanvas.height = warpedCanvas.width;
            } else { //reverted
              rotatedCanvas.width = warpedCanvas.width;
              rotatedCanvas.height = warpedCanvas.height;
            }

            // Apply rotation
            rotCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
            rotCtx.rotate((rotationDegrees * Math.PI) / 180);
            rotCtx.drawImage(
              warpedCanvas,
              -warpedCanvas.width / 2,
              -warpedCanvas.height / 2,
            );

            const warpedImageData = rotatedCanvas.toDataURL();
            setWarpedImage(warpedImageData);
            if (onContinue) {
              onContinue(warpedImageData, calculatedVehicleID);
            }
          } else {
            const warpedImageData = warpedCanvas.toDataURL();
            setWarpedImage(warpedImageData);
            if (onContinue) {
              onContinue(warpedImageData, calculatedVehicleID);
            }
          }
        } catch (error) {
          console.error("Perspective warp failed:", error);
          setWarpedImage(null);
          setHasError(2);
        }
      }

      setIsLoading(false);
    };
    
    img.src = imageUrl;
  }, [imageUrl, onMarkersDetected]);

  return (
    <div>
      <canvas ref={canvasRef} style={{ display:"none" }} />

      {isLoading && (
        <div className="loading-container">
          <p className="loading-text">Bild wird verarbeitet...</p>
          <div className="loading-bar-track">
            <div className="loading-bar-fill" />
          </div>
        </div>
      )}

      {!isLoading && !warpedImage && (
        <div className="error-message">
          <h3>Fehler: {
              (hasError === 1) ? "Fehler beim Laden des Bildes." : 
              (hasError === 2) ? "Nicht alle Marker wurden erkannt\n[Error code: 2]" :
              (hasError === 3) ? "Nicht alle Marker wurden erkannt\n[Error code: 3]" :
              ("" + hasError)
            }</h3>
        </div>
      )}

      {!isLoading && warpedImage && (
        <div>
          <img
            src={warpedImage}
            alt="Warped marker"
            className="image-preview"
          />
        </div>
      )}
    </div>
  );
}

export default MarkerCanvas;
